/**
 * UpgradePickup — a slow-drifting capsule that grants one upgrade when
 * touched by the player.
 *
 * One drops per wave (see GameScene.maybeDropUpgrade). The pickup id is
 * chosen by UpgradeSystem.nextDropUpgradeId() so the player always sees
 * meaningful progression. The pickup shows its label (MULTISHOT, SPREAD,
 * FIRE RATE, BIG BULLETS) in the centre of the capsule.
 */
import Phaser from "phaser";
import { UPGRADE_LABELS, type UpgradeId } from "../systems/upgradeIds";

const PICKUP_TEXTURE = "trashstock_logo";

export class UpgradePickup extends Phaser.Physics.Arcade.Sprite {
  public upgradeId: UpgradeId | "LIFE" | null = null;
  public bornAt = 0;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    // Texture is generated by GameScene.create() before the first pickup
    // spawns — fallback to a placeholder texture name so the type is
    // declared even before the texture exists.
    super(scene, x, y, PICKUP_TEXTURE);
    this.setDisplaySize(32, 32);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(32, 32); // matches the visible pickup capsule.
    this.setImmovable(true);
    this.setDepth(11);
  }

  public configure(id: UpgradeId | "LIFE", now: number): void {
    this.upgradeId = id;
    this.bornAt = now;
    const text = id === "LIFE" ? "LIFE" : UPGRADE_LABELS[id as UpgradeId];
    const label = this.scene.add
      .text(this.x, this.y + 24, text, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: "10px",
        color: "#ffffff",
      })
      .setOrigin(0.5, 0.5);
    label.setName("pickup-label");
    label.setDepth(11);
    this.setData("label", label);
    this.setActive(true);
    this.setVisible(true);
    
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.reset(this.x, this.y);
    
    this.setVelocity(-90, 0); // drifts left slowly
  }

  public update(now: number): void {
    if (!this.active) return;
    // Float the label with the pickup so it follows the sprite.
    const label = this.getData("label") as Phaser.GameObjects.Text | undefined;
    if (label) {
      label.x = this.x;
      label.y = this.y + 32;
    }
    // Pulse and flicker the pickup so it reads as "pick me up".
    const baseSize = 60;
    const s = baseSize * (1 + 0.15 * Math.sin(now * 0.005));
    this.setDisplaySize(s, s);
    this.setAlpha(Math.sin(now / 50) > 0 ? 1 : 0.4); // flicker
  }

  public collect(): void {
    const label = this.getData("label") as Phaser.GameObjects.Text | undefined;
    if (label) label.destroy();
    this.disableBody(true, true);
  }
}